import type {
  MeshOutboxConfig,
  MeshOutboxGlobal,
  MeshOutboxQueueStats,
} from '@/types/control/mesh-outbox';

import { ControlApiError, controlApiRequest } from './control-api-client';

/**
 * The mesh outbox admin surface.
 *
 * Only `/global` and `/tenants` are wrapped. `GET /mesh-outbox/tenants/{id}`
 * is deliberately absent: it hardcodes `entries: nil` in every branch, and its
 * only other payload is byte-identical to the row already in the `/tenants`
 * array — so it costs a request and returns nothing new. That is intended, not
 * a bug: outbox entries live in the worker's memory and in ephemeral Pebble on
 * the worker's own disk, neither of which the api process can reach. Entry-level
 * drill-in needs worker-side work and is a separate ticket.
 */

const BASE = '/api/admin/mesh-outbox';

/** The machine code the API uses when the snapshot reader is not wired. */
const UNAVAILABLE_CODE = 'mesh_outbox_unavailable';

/**
 * Whether a failure means the outbox surface is not wired at all, as opposed to
 * a transient error. Matches on the typed code rather than on prose.
 */
export function isOutboxUnavailable(error: unknown): boolean {
  return error instanceof ControlApiError && error.code === UNAVAILABLE_CODE;
}

type GlobalResponse = {
  configured_rate_per_sec?: unknown;
  configured_burst_size?: unknown;
  drainer_enabled?: unknown;
  observed_rate_per_sec?: unknown;
  tenants_with_backlog?: unknown;
  total_pending?: unknown;
  total_in_flight?: unknown;
  total_failed?: unknown;
  snapshot_as_of?: unknown;
};

type QueueStatsResponse = {
  tenant_id?: unknown;
  pending?: unknown;
  in_flight?: unknown;
  sent?: unknown;
  failed?: unknown;
  total?: unknown;
  oldest_pending_age_ms?: unknown;
  newest_enqueued_at?: unknown;
  last_enqueued_at?: unknown;
  last_drain_idle_at?: unknown;
};

function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** Absent rather than null on the wire, so an empty string is also "unset". */
function asOptionalStamp(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

const NANOS_PER_MS = 1_000_000;

function toConfig(res: GlobalResponse): MeshOutboxConfig {
  return {
    ratePerSec: asNumber(res.configured_rate_per_sec),
    burstSize: asNumber(res.configured_burst_size),
    drainerEnabled: res.drainer_enabled === true,
  };
}

function toQueueStats(res: QueueStatsResponse): MeshOutboxQueueStats {
  return {
    tenantId: asString(res.tenant_id),
    pending: asNumber(res.pending),
    inFlight: asNumber(res.in_flight),
    sent: asNumber(res.sent),
    failed: asNumber(res.failed),
    total: asNumber(res.total),
    // The JSON tag says ms; the Go field is a time.Duration, which is ns.
    oldestPendingAgeMs: Math.round(
      asNumber(res.oldest_pending_age_ms) / NANOS_PER_MS
    ),
    newestEnqueuedAt: asOptionalStamp(res.newest_enqueued_at),
    lastEnqueuedAt: asOptionalStamp(res.last_enqueued_at),
    lastDrainIdleAt: asOptionalStamp(res.last_drain_idle_at),
  };
}

export const meshOutboxService = {
  /**
   * `snapshot_as_of` is the discriminant, not `snapshot_age_ms` — that field is
   * a literal `nil` in Go and is never a number despite its name, so the page
   * computes staleness itself.
   */
  async getGlobal(signal?: AbortSignal): Promise<MeshOutboxGlobal> {
    const res = await controlApiRequest<GlobalResponse | null>({
      path: `${BASE}/global`,
      signal,
    });

    const config = toConfig(res ?? {});
    const asOf = asOptionalStamp(res?.snapshot_as_of);

    if (!asOf) {
      return { kind: 'no-snapshot', config };
    }

    return {
      kind: 'snapshot',
      config,
      asOf,
      totals: {
        observedRatePerSec: asNumber(res?.observed_rate_per_sec),
        tenantsWithBacklog: asNumber(res?.tenants_with_backlog),
        totalPending: asNumber(res?.total_pending),
        totalInFlight: asNumber(res?.total_in_flight),
        totalFailed: asNumber(res?.total_failed),
      },
    };
  },

  async listTenants(signal?: AbortSignal): Promise<MeshOutboxQueueStats[]> {
    const res = await controlApiRequest<QueueStatsResponse[] | null>({
      path: `${BASE}/tenants`,
      signal,
    });

    // The handler emits `[]` rather than null, but the client is a passthrough
    // for anything upstream changes.
    if (!Array.isArray(res)) {
      return [];
    }

    return res.map(toQueueStats);
  },
};
